import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  MessageSquareText,
  Search,
  X,
} from 'lucide-react';
import { useUsageEvents, useUsagePrices, useUsageSummary, useUsageTurn, useUserUsage } from '../hooks';
import { useAuth } from '../contexts/AuthContext';
import type {
  CostGroup,
  TurnCost,
  UsageEvent,
  UsageEventFilters,
  UsagePeriod,
  UserSort,
  UserUsage,
} from '../api/usage';
import { getErrorDetail } from '../utils/error';
import {
  KIND_ORDER,
  daysBetween,
  formatInt,
  formatKrw,
  formatKstTime,
  formatUsd,
  kindColor,
  totalTokens,
} from '../utils/usage';

const USERS_PAGE = 50;
const EVENTS_PAGE = 100;

// --- 공통 조각 -----------------------------------------------------------------------

const Money: React.FC<{ usd: number; rate: number; className?: string }> = ({ usd, rate, className }) => (
  <span className={`tabular-nums ${className ?? ''}`} title={formatUsd(usd)}>
    {formatKrw(usd, rate)}
  </span>
);

const Loading: React.FC = () => (
  <div className="flex justify-center py-16">
    <Loader2 className="animate-spin text-slate-400" size={28} />
  </div>
);

const ErrorBox: React.FC<{ error: unknown; fallback: string }> = ({ error, fallback }) => (
  <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
    {getErrorDetail(error, fallback)}
  </p>
);

const KindDot: React.FC<{ kind: string }> = ({ kind }) => (
  <span
    className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
    style={{ backgroundColor: kindColor(kind) }}
    aria-hidden
  />
);

const Stat: React.FC<{ label: string; children: React.ReactNode; hint?: React.ReactNode }> = ({
  label,
  children,
  hint,
}) => (
  <div className="card p-4">
    <p className="text-xs font-medium text-slate-500">{label}</p>
    <div className="text-2xl font-semibold text-slate-800 mt-1">{children}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

const shortId = (id: string) => id.slice(0, 8);

const hostOf = (url: string) => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

const useFeatureLabel = () => {
  const { t } = useTranslation();
  return (feature: string) => t(`usage.features.${feature}`, { defaultValue: feature });
};

const useKindLabel = () => {
  const { t } = useTranslation();
  return (kind: string) => t(`usage.kinds.${kind}`, { defaultValue: kind });
};

// --- 요약 ------------------------------------------------------------------------------

interface SummaryPanelProps {
  period: UsagePeriod;
  from: string;
  to: string;
  onOpenEvents: (filters: UsageEventFilters) => void;
  /** 주면 그 사용자 한 명의 요약 — 무료 한도는 빼지 않는다 */
  userId?: string;
}

const GroupTable: React.FC<{
  title: string;
  groups: CostGroup[];
  total: number;
  rate: number;
  label: (key: string) => React.ReactNode;
  onSelect?: (key: string) => void;
  testId: string;
}> = ({ title, groups, total, rate, label, onSelect, testId }) => {
  const { t } = useTranslation();
  return (
    <div className="card p-4" data-testid={testId}>
      <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>
      {groups.length === 0 ? (
        <p className="text-sm text-slate-400">{t('usage.noData')}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 text-left">
              <th className="font-medium pb-2">{t('usage.cols.name')}</th>
              <th className="font-medium pb-2 text-right">{t('usage.cols.calls')}</th>
              <th className="font-medium pb-2 text-right">{t('usage.cols.tokens')}</th>
              <th className="font-medium pb-2 text-right">{t('usage.cols.perCall')}</th>
              <th className="font-medium pb-2 text-right">{t('usage.cols.cost')}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const share = total > 0 ? g.cost_usd / total : 0;
              return (
                <tr
                  key={g.key}
                  onClick={onSelect ? () => onSelect(g.key) : undefined}
                  className={`border-t border-slate-100 ${onSelect ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                >
                  <td className="py-1.5 pr-2">
                    <div>{label(g.key)}</div>
                    <div className="h-1 mt-1 rounded-full bg-slate-100 overflow-hidden" aria-hidden>
                      <div className="h-full rounded-full bg-blue-500" style={{ width: `${share * 100}%` }} />
                    </div>
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{formatInt(g.calls)}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {totalTokens(g) > 0 ? formatInt(totalTokens(g)) : g.units > 0 ? `${g.units} u` : '-'}
                  </td>
                  <td className="py-1.5 text-right text-slate-600">
                    <Money usd={g.calls ? g.cost_usd / g.calls : 0} rate={rate} />
                  </td>
                  <td className="py-1.5 text-right">
                    <Money usd={g.cost_usd} rate={rate} />
                    <span className="text-xs text-slate-400 ml-1 tabular-nums">{Math.round(share * 100)}%</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

/** 채팅 한 번(턴)에 평균 얼마 — 같은 턴의 채팅 모델·도구·카드·대화 뒤 처리까지 더한 값. */
const TurnCostCard: React.FC<{ turns: TurnCost; rate: number }> = ({ turns, rate }) => {
  const { t } = useTranslation();
  const parts = [
    [t('usage.turnParts.chatLlm'), turns.avg_chat_llm_usd],
    [t('usage.turnParts.tools'), turns.avg_tools_usd],
    [t('usage.turnParts.otherLlm'), turns.avg_other_llm_usd],
  ] as const;
  return (
    <div className="card p-4" data-testid="usage-turn-cost">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-700">{t('usage.turnCostTitle')}</h3>
        <span className="text-xs text-slate-500">
          {t('usage.turnCostMeta', { turns: formatInt(turns.turns), total: formatKrw(turns.total_usd, rate) })}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-x-8 gap-y-3">
        <div>
          <p className="text-xs text-slate-500">{t('usage.turnAvg')}</p>
          <p className="text-2xl font-semibold text-slate-800">
            <Money usd={turns.avg_usd} rate={rate} />
          </p>
        </div>
        {parts.map(([label, usd]) => (
          <div key={label}>
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-lg font-medium text-slate-700">
              <Money usd={usd} rate={rate} />
              <span className="text-xs text-slate-400 ml-1 tabular-nums">
                {turns.avg_usd > 0 ? Math.round((usd / turns.avg_usd) * 100) : 0}%
              </span>
            </p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-2">{t('usage.turnCostNote')}</p>
    </div>
  );
};

export const SummaryPanel: React.FC<SummaryPanelProps> = ({ period, from, to, onOpenEvents, userId }) => {
  const { t } = useTranslation();
  const featureLabel = useFeatureLabel();
  const kindLabel = useKindLabel();
  const summary = useUsageSummary({ ...period, user_id: userId }, true);
  // 사용자 한 명 요약에서 표를 누르면 그 사용자 호출로 좁혀 연다.
  const openEvents = (filters: UsageEventFilters) => onOpenEvents(userId ? { ...filters, user_id: userId } : filters);

  const chart = useMemo(() => {
    const data = summary.data;
    if (!data) return { rows: [], kinds: [] as string[] };
    const kinds = [...new Set(data.daily.map((d) => d.kind))].sort(
      (a, b) => (KIND_ORDER.indexOf(a) + 1 || 99) - (KIND_ORDER.indexOf(b) + 1 || 99),
    );
    const byDay = new Map<string, Record<string, number>>();
    for (const d of data.daily) {
      const row = byDay.get(d.day) ?? {};
      row[d.kind] = d.cost_usd * data.usd_krw_rate;
      byDay.set(d.day, row);
    }
    const rows = daysBetween(from, to).map((day) => ({ day: day.slice(5), ...(byDay.get(day) ?? {}) }));
    return { rows, kinds };
  }, [summary.data, from, to]);

  if (summary.isLoading) return <Loading />;
  if (summary.isError || !summary.data) return <ErrorBox error={summary.error} fallback={t('usage.loadFailed')} />;
  const s = summary.data;
  const rate = s.usd_krw_rate;
  const freeDiscount = s.free_tiers.reduce((acc, f) => acc + f.discount_usd, 0);

  return (
    <div className="space-y-4">
      {(s.unpriced_calls > 0 || s.unscoped_calls > 0) && (
        <div className="flex gap-3 items-start text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div className="space-y-1">
            {s.unpriced_calls > 0 && <p>{t('usage.unpricedWarning', { count: s.unpriced_calls })}</p>}
            {s.unscoped_calls > 0 && <p>{t('usage.unscopedWarning', { count: s.unscoped_calls })}</p>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={t('usage.totalCost')} hint={formatUsd(s.cost_usd)}>
          <Money usd={s.cost_usd} rate={rate} />
        </Stat>
        {userId ? (
          <Stat label={t('usage.turnAvgStat')} hint={t('usage.turnsCount', { count: s.chat_turns?.turns ?? 0 })}>
            {s.chat_turns ? <Money usd={s.chat_turns.avg_usd} rate={rate} /> : '-'}
          </Stat>
        ) : (
          <Stat
            label={t('usage.billedEstimate')}
            hint={
              freeDiscount > 0
                ? t('usage.freeTierDiscount', { amount: formatKrw(freeDiscount, rate) })
                : formatUsd(s.billed_estimate_usd)
            }
          >
            <Money usd={s.billed_estimate_usd} rate={rate} />
          </Stat>
        )}
        <Stat label={t('usage.callsAndUsers')} hint={userId ? undefined : t('usage.usersCount', { count: s.users })}>
          <span className="tabular-nums">{formatInt(s.calls)}</span>
        </Stat>
        <Stat
          label={t('usage.cacheHitRate')}
          hint={t('usage.tokenBreakdown', {
            input: formatInt(s.input_tokens),
            cache: formatInt(s.cache_read_tokens),
            output: formatInt(s.output_tokens + s.reasoning_tokens),
          })}
        >
          <span className="tabular-nums">
            {s.cache_hit_rate === null ? '-' : `${(s.cache_hit_rate * 100).toFixed(1)}%`}
          </span>
        </Stat>
      </div>

      {s.chat_turns && <TurnCostCard turns={s.chat_turns} rate={rate} />}

      <div className="card p-4" data-testid="usage-daily-chart">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-sm font-semibold text-slate-700">{t('usage.dailyCost')}</h3>
          <div className="flex flex-wrap gap-3 text-xs text-slate-600">
            {chart.kinds.map((k) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <KindDot kind={k} />
                {kindLabel(k)}
              </span>
            ))}
          </div>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart.rows} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickLine={false}
                axisLine={false}
                width={56}
                tickFormatter={(v: number) => `₩${Math.round(v).toLocaleString('ko-KR')}`}
              />
              <Tooltip
                cursor={{ fill: '#f1f5f9' }}
                formatter={(value: number, name: string) => [`₩${value.toFixed(value < 10 ? 2 : 0)}`, kindLabel(name)]}
                labelStyle={{ color: '#334155', fontWeight: 600 }}
                contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: '#e2e8f0' }}
              />
              {chart.kinds.map((k) => (
                <Bar
                  key={k}
                  dataKey={k}
                  stackId="cost"
                  fill={kindColor(k)}
                  stroke="#ffffff"
                  strokeWidth={1}
                  maxBarSize={28}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <GroupTable
          title={t('usage.byFeature')}
          groups={s.by_feature}
          total={s.cost_usd}
          rate={rate}
          testId="usage-by-feature"
          label={(key) => (
            <>
              {featureLabel(key)} <span className="text-xs text-slate-400 font-mono">{key}</span>
            </>
          )}
          onSelect={(feature) => openEvents({ feature })}
        />
        <GroupTable
          title={t('usage.byModel')}
          groups={s.by_model}
          total={s.cost_usd}
          rate={rate}
          testId="usage-by-model"
          label={(key) => <span className="font-mono text-xs">{key}</span>}
          onSelect={(key) => openEvents({ model: key.slice(key.indexOf('/') + 1) })}
        />
        <GroupTable
          title={t('usage.byKind')}
          groups={s.by_kind}
          total={s.cost_usd}
          rate={rate}
          testId="usage-by-kind"
          label={(key) => (
            <span className="inline-flex items-center gap-1.5">
              <KindDot kind={key} />
              {kindLabel(key)}
            </span>
          )}
          onSelect={(kind) => openEvents({ kind })}
        />
        {!userId && (
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('usage.freeTiers')}</h3>
            <table className="w-full text-sm">
              <tbody>
                {s.free_tiers.map((f) => (
                  <tr key={f.label} className="border-t border-slate-100 first:border-0">
                    <td className="py-1.5 pr-2">{f.label}</td>
                    <td className="py-1.5 text-right tabular-nums text-slate-600">
                      {t('usage.freeTierUsed', {
                        used: formatInt(f.units_in_period),
                        applied: formatInt(f.free_units_applied),
                      })}
                    </td>
                    <td className="py-1.5 text-right">
                      {f.discount_usd > 0 ? (
                        <>
                          -<Money usd={f.discount_usd} rate={rate} />
                        </>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-slate-500 mt-3">{t('usage.freeTierNote')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// --- 사용자 ----------------------------------------------------------------------------

const SortHeader: React.FC<{
  id: UserSort;
  label: string;
  sort: UserSort;
  desc: boolean;
  onSort: (id: UserSort) => void;
}> = ({ id, label, sort, desc, onSort }) => (
  <th className="font-medium py-2 px-3 text-right">
    <button type="button" onClick={() => onSort(id)} className="inline-flex items-center gap-1 hover:text-slate-800">
      {label}
      {sort === id && (desc ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
    </button>
  </th>
);

/** 사용자 한 명 — 요약 탭과 같은 화면을 그 사람 호출만으로. */
const UserDetail: React.FC<{
  user: UserUsage;
  period: UsagePeriod;
  from: string;
  to: string;
  onBack: () => void;
  onOpenEvents: (f: UsageEventFilters) => void;
}> = ({ user, period, from, to, onBack, onOpenEvents }) => {
  const { t } = useTranslation();
  const userId = user.user_id ?? 'none';
  return (
    <div className="space-y-4" data-testid="usage-user-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onBack} className="btn-secondary text-sm inline-flex items-center gap-1.5">
            <ArrowLeft size={15} /> {t('usage.backToUsers')}
          </button>
          <div>
            <p className="text-lg font-semibold text-slate-800">
              {user.user_id ? user.name || t('usage.noName') : t('usage.noUser')}
              {user.tier_id && <span className="ml-2 text-xs font-medium text-slate-500">{user.tier_id}</span>}
            </p>
            {user.user_id && <p className="text-xs text-slate-400 font-mono">{user.user_id}</p>}
          </div>
        </div>
        <button type="button" className="btn-secondary text-sm" onClick={() => onOpenEvents({ user_id: userId })}>
          {t('usage.openUserEvents')}
        </button>
      </div>
      <SummaryPanel period={period} from={from} to={to} userId={userId} onOpenEvents={onOpenEvents} />
    </div>
  );
};

export const UsersPanel: React.FC<{
  period: UsagePeriod;
  from: string;
  to: string;
  onOpenEvents: (f: UsageEventFilters) => void;
}> = ({ period, from, to, onOpenEvents }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<UserUsage | null>(null);
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<UserSort>('cost');
  const [desc, setDesc] = useState(true);
  const [page, setPage] = useState(0);
  const users = useUserUsage({ ...period, q, sort, desc, limit: USERS_PAGE, offset: page * USERS_PAGE }, true);

  const sortBy = (key: UserSort) => {
    if (key === sort) setDesc((d) => !d);
    else {
      setSort(key);
      setDesc(true);
    }
    setPage(0);
  };

  const sortProps = { sort, desc, onSort: sortBy };
  const data = users.data;
  const rate = data?.usd_krw_rate ?? 0;
  const pages = data ? Math.max(1, Math.ceil(data.total / USERS_PAGE)) : 1;

  if (selected)
    return (
      <UserDetail
        user={selected}
        period={period}
        from={from}
        to={to}
        onBack={() => setSelected(null)}
        onOpenEvents={onOpenEvents}
      />
    );

  return (
    <div className="space-y-3">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQ(draft.trim());
          setPage(0);
        }}
      >
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-9 text-sm"
            placeholder={t('usage.userSearch')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
        <button type="submit" className="btn-secondary text-sm">
          {t('usage.search')}
        </button>
      </form>

      {users.isError && <ErrorBox error={users.error} fallback={t('usage.loadFailed')} />}
      {users.isLoading ? (
        <Loading />
      ) : (
        data && (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm" data-testid="usage-users-table">
              <thead className="bg-slate-50 text-xs text-slate-500 whitespace-nowrap">
                <tr>
                  <th className="font-medium py-2 px-3 text-left">{t('usage.cols.user')}</th>
                  <th className="font-medium py-2 px-3 text-left">{t('usage.cols.tier')}</th>
                  <SortHeader id="calls" label={t('usage.cols.calls')} {...sortProps} />
                  <SortHeader id="chat_turns" label={t('usage.cols.chatTurns')} {...sortProps} />
                  <SortHeader id="tokens" label={t('usage.cols.tokens')} {...sortProps} />
                  <SortHeader id="cost" label={t('usage.cols.cost')} {...sortProps} />
                  <th className="font-medium py-2 px-3 text-right">{t('usage.cols.costPerTurn')}</th>
                  <SortHeader id="last_at" label={t('usage.cols.lastAt')} {...sortProps} />
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-slate-400">
                      {t('usage.noData')}
                    </td>
                  </tr>
                )}
                {data.items.map((u) => (
                  <tr
                    key={u.user_id ?? 'none'}
                    onClick={() => setSelected(u)}
                    className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                  >
                    <td className="py-2 px-3">
                      {u.user_id ? (
                        <>
                          <span className="font-medium text-slate-800">{u.name || t('usage.noName')}</span>{' '}
                          <span className="text-xs text-slate-400 font-mono">{shortId(u.user_id)}</span>
                        </>
                      ) : (
                        <span className="text-slate-500 italic">{t('usage.noUser')}</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-slate-600">{u.tier_id ?? '-'}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{formatInt(u.calls)}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{formatInt(u.chat_turns)}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{formatInt(u.total_tokens)}</td>
                    <td className="py-2 px-3 text-right font-medium">
                      <Money usd={u.cost_usd} rate={rate} />
                    </td>
                    <td className="py-2 px-3 text-right">
                      {u.cost_per_chat_turn_usd === null ? '-' : <Money usd={u.cost_per_chat_turn_usd} rate={rate} />}
                    </td>
                    <td className="py-2 px-3 text-right text-slate-500 tabular-nums">{formatKstTime(u.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {data && data.total > USERS_PAGE && <Pager page={page} pages={pages} onChange={setPage} total={data.total} />}
      <p className="text-xs text-slate-500">{t('usage.costPerTurnNote')}</p>
    </div>
  );
};

const Pager: React.FC<{ page: number; pages: number; total: number; onChange: (p: number) => void }> = ({
  page,
  pages,
  total,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-end gap-2 text-sm text-slate-600">
      <span className="tabular-nums">{t('usage.pageInfo', { page: page + 1, pages, total: formatInt(total) })}</span>
      <button type="button" className="btn-secondary text-sm" disabled={page === 0} onClick={() => onChange(page - 1)}>
        {t('usage.prev')}
      </button>
      <button
        type="button"
        className="btn-secondary text-sm"
        disabled={page + 1 >= pages}
        onClick={() => onChange(page + 1)}
      >
        {t('usage.next')}
      </button>
    </div>
  );
};

// --- 호출 ------------------------------------------------------------------------------

const FILTER_KEYS = ['user_id', 'feature', 'kind', 'model', 'thread_id', 'request_id'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

interface EventGroup {
  key: string;
  events: UsageEvent[];
}

/** 같은 request_id 는 한 턴 — 한 페이지 안에서 처음 나온 자리에 모아 접는다. */
const groupByRequest = (events: UsageEvent[]): EventGroup[] => {
  const groups: EventGroup[] = [];
  const index = new Map<string, EventGroup>();
  for (const e of events) {
    const existing = e.request_id ? index.get(e.request_id) : undefined;
    if (existing) {
      existing.events.push(e);
      continue;
    }
    const group = { key: e.request_id ?? e.id, events: [e] };
    groups.push(group);
    if (e.request_id) index.set(e.request_id, group);
  }
  return groups;
};

interface EventsPanelProps {
  period: UsagePeriod;
  filters: UsageEventFilters;
  onFiltersChange: (f: UsageEventFilters) => void;
}

export const EventsPanel: React.FC<EventsPanelProps> = ({ period, filters, onFiltersChange }) => {
  const { t } = useTranslation();
  const featureLabel = useFeatureLabel();
  const [draft, setDraft] = useState<UsageEventFilters>(filters);
  const [page, setPage] = useState(0);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [openEvent, setOpenEvent] = useState<string | null>(null);
  const filterKey = JSON.stringify(filters);
  const [seenKey, setSeenKey] = useState(filterKey);
  // 바깥(요약·사용자 탭)에서 조건이 바뀌면 입력 칸과 페이지를 맞춘다.
  if (seenKey !== filterKey) {
    setSeenKey(filterKey);
    setDraft(filters);
    setPage(0);
  }

  const events = useUsageEvents({ ...period, ...filters, limit: EVENTS_PAGE, offset: page * EVENTS_PAGE }, true);
  const data = events.data;
  const groups = useMemo(() => groupByRequest(data?.items ?? []), [data]);
  const pages = data ? Math.max(1, Math.ceil(data.total / EVENTS_PAGE)) : 1;

  const apply = (next: UsageEventFilters) => {
    const cleaned = Object.fromEntries(
      Object.entries(next)
        .filter(([, v]) => typeof v === 'string' && v.trim() !== '')
        .map(([k, v]) => [k, (v as string).trim()]),
    ) as UsageEventFilters;
    onFiltersChange(cleaned);
    setOpenEvent(null);
    setOpenGroups(new Set());
  };

  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const active = FILTER_KEYS.filter((k) => filters[k]);

  return (
    <div className="space-y-3">
      <form
        className="card p-4 grid grid-cols-2 lg:grid-cols-6 gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          apply(draft);
        }}
      >
        {FILTER_KEYS.map((k) => (
          <label key={k} className="text-xs text-slate-500 space-y-1">
            <span>{t(`usage.filters.${k}`)}</span>
            <input
              className="input text-sm py-1.5 font-mono"
              value={draft[k] ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
              placeholder={k === 'user_id' ? 'uuid · none' : ''}
            />
          </label>
        ))}
        <div className="col-span-2 lg:col-span-6 flex flex-wrap items-center gap-2">
          <button type="submit" className="btn-primary text-sm">
            {t('usage.applyFilters')}
          </button>
          {active.map((k: FilterKey) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 ring-1 ring-blue-200 rounded-full pl-2.5 pr-1 py-0.5"
            >
              {t(`usage.filters.${k}`)}: <span className="font-mono">{filters[k]}</span>
              <button
                type="button"
                aria-label={t('usage.removeFilter')}
                onClick={() => apply({ ...filters, [k]: '' })}
                className="p-0.5 rounded-full hover:bg-blue-100"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      </form>

      {events.isError && <ErrorBox error={events.error} fallback={t('usage.loadFailed')} />}
      {events.isLoading ? (
        <Loading />
      ) : (
        data && (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm" data-testid="usage-events-table">
              <thead className="bg-slate-50 text-xs text-slate-500 whitespace-nowrap">
                <tr>
                  <th className="w-6" />
                  <th className="font-medium py-2 px-2 text-left">{t('usage.cols.time')}</th>
                  <th className="font-medium py-2 px-2 text-left">{t('usage.cols.user')}</th>
                  <th className="font-medium py-2 px-2 text-left">{t('usage.cols.feature')}</th>
                  <th className="font-medium py-2 px-2 text-left">{t('usage.cols.model')}</th>
                  <th className="font-medium py-2 px-2 text-right">{t('usage.cols.input')}</th>
                  <th className="font-medium py-2 px-2 text-right">{t('usage.cols.cacheRead')}</th>
                  <th className="font-medium py-2 px-2 text-right">{t('usage.cols.cacheWrite')}</th>
                  <th className="font-medium py-2 px-2 text-right">{t('usage.cols.output')}</th>
                  <th className="font-medium py-2 px-2 text-right">{t('usage.cols.reasoning')}</th>
                  <th className="font-medium py-2 px-2 text-right">{t('usage.cols.units')}</th>
                  <th className="font-medium py-2 px-2 text-right">{t('usage.cols.cost')}</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 && (
                  <tr>
                    <td colSpan={12} className="py-10 text-center text-slate-400">
                      {t('usage.noData')}
                    </td>
                  </tr>
                )}
                {groups.map((g) => {
                  if (g.events.length === 1)
                    return (
                      <EventRows
                        key={g.key}
                        event={g.events[0]}
                        rate={data.usd_krw_rate}
                        langfuseProjectId={data.langfuse_project_id}
                        open={openEvent === g.events[0].id}
                        onToggle={() => setOpenEvent((cur) => (cur === g.events[0].id ? null : g.events[0].id))}
                        onFilter={(f) => apply({ ...filters, ...f })}
                      />
                    );
                  const isOpen = openGroups.has(g.key);
                  const first = g.events[g.events.length - 1];
                  const cost = g.events.reduce((acc, e) => acc + e.cost_usd, 0);
                  const features = [...new Set(g.events.map((e) => featureLabel(e.feature)))];
                  return (
                    <React.Fragment key={g.key}>
                      <tr
                        className="border-t border-slate-200 bg-slate-50/60 cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleGroup(g.key)}
                        data-testid="usage-turn-group"
                      >
                        <td className="pl-2 text-slate-400">
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </td>
                        <td className="py-2 px-2 tabular-nums text-slate-600">{formatKstTime(first.occurred_at)}</td>
                        <td className="py-2 px-2">
                          <UserCell event={first} />
                        </td>
                        <td className="py-2 px-2" colSpan={9}>
                          <span className="font-medium text-slate-700">
                            {t('usage.turnCalls', { count: g.events.length })}
                          </span>
                          <span className="text-slate-500"> · {features.join(', ')}</span>
                        </td>
                        <td className="py-2 px-2 text-right font-medium">
                          <Money usd={cost} rate={data.usd_krw_rate} />
                        </td>
                      </tr>
                      {isOpen &&
                        [...g.events]
                          .reverse()
                          .map((e) => (
                            <EventRows
                              key={e.id}
                              event={e}
                              nested
                              rate={data.usd_krw_rate}
                              langfuseProjectId={data.langfuse_project_id}
                              open={openEvent === e.id}
                              onToggle={() => setOpenEvent((cur) => (cur === e.id ? null : e.id))}
                              onFilter={(f) => apply({ ...filters, ...f })}
                            />
                          ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
      {data && data.total > EVENTS_PAGE && <Pager page={page} pages={pages} total={data.total} onChange={setPage} />}
    </div>
  );
};

const UserCell: React.FC<{ event: UsageEvent }> = ({ event }) => {
  const { t } = useTranslation();
  if (!event.user_id) return <span className="text-slate-400 italic">{t('usage.noUser')}</span>;
  return (
    <span>
      {event.user_name || t('usage.noName')}{' '}
      <span className="text-xs text-slate-400 font-mono">{shortId(event.user_id)}</span>
    </span>
  );
};

const num = (n: number) => (n ? formatInt(n) : <span className="text-slate-300">0</span>);

const EventRows: React.FC<{
  event: UsageEvent;
  rate: number;
  langfuseProjectId: string | null;
  open: boolean;
  nested?: boolean;
  onToggle: () => void;
  onFilter: (f: UsageEventFilters) => void;
}> = ({ event: e, rate, langfuseProjectId, open, nested, onToggle, onFilter }) => {
  const featureLabel = useFeatureLabel();
  return (
    <>
      <tr
        className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${open ? 'bg-blue-50/40' : ''}`}
        onClick={onToggle}
        data-testid="usage-event-row"
      >
        <td className={`text-slate-400 ${nested ? 'pl-5' : 'pl-2'}`}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="py-1.5 px-2 tabular-nums text-slate-600 whitespace-nowrap">{formatKstTime(e.occurred_at)}</td>
        <td className="py-1.5 px-2 whitespace-nowrap">
          <UserCell event={e} />
        </td>
        <td className="py-1.5 px-2 whitespace-nowrap">
          <span className="inline-flex items-center gap-1.5">
            <KindDot kind={e.kind} />
            {featureLabel(e.feature)}
          </span>
        </td>
        <td className="py-1.5 px-2 font-mono text-xs text-slate-600">{e.model}</td>
        <td className="py-1.5 px-2 text-right tabular-nums">{num(e.input_tokens)}</td>
        <td className="py-1.5 px-2 text-right tabular-nums">{num(e.cache_read_tokens)}</td>
        <td className="py-1.5 px-2 text-right tabular-nums">{num(e.cache_write_tokens)}</td>
        <td className="py-1.5 px-2 text-right tabular-nums">{num(e.output_tokens)}</td>
        <td className="py-1.5 px-2 text-right tabular-nums">{num(e.reasoning_tokens)}</td>
        <td className="py-1.5 px-2 text-right tabular-nums">
          {e.units ? e.units : <span className="text-slate-300">0</span>}
        </td>
        <td className="py-1.5 px-2 text-right whitespace-nowrap">
          {!e.priced && <AlertTriangle size={12} className="inline text-amber-500 mr-1" aria-label="unpriced" />}
          <Money usd={e.cost_usd} rate={rate} />
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50/70">
          <td />
          <td colSpan={11} className="px-2 py-3">
            <EventDetail event={e} langfuseProjectId={langfuseProjectId} onFilter={onFilter} />
          </td>
        </tr>
      )}
    </>
  );
};

const EventDetail: React.FC<{
  event: UsageEvent;
  langfuseProjectId: string | null;
  onFilter: (f: UsageEventFilters) => void;
}> = ({ event: e, langfuseProjectId, onFilter }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canReadContent = user?.role === 'admin' || user?.role === 'super_admin';
  const [showTurn, setShowTurn] = useState(false);
  const traceUrl =
    e.trace_id && langfuseProjectId
      ? `${window.location.protocol}//${window.location.hostname}:3000/project/${langfuseProjectId}/traces/${e.trace_id}`
      : null;

  const idLink = (label: string, value: string | null, filter: UsageEventFilters) =>
    value && (
      <div className="flex items-center gap-2">
        <span className="text-slate-500 w-20 shrink-0">{label}</span>
        <span className="font-mono text-xs break-all">{value}</span>
        <button
          type="button"
          className="text-xs text-blue-600 hover:underline shrink-0"
          onClick={() => onFilter(filter)}
        >
          {t('usage.filterThis')}
        </button>
      </div>
    );

  return (
    <div className="space-y-3 text-sm" data-testid="usage-event-detail">
      <div className="grid lg:grid-cols-2 gap-x-6 gap-y-1">
        {idLink(t('usage.filters.user_id'), e.user_id, { user_id: e.user_id ?? undefined })}
        {idLink(t('usage.filters.thread_id'), e.thread_id, { thread_id: e.thread_id ?? undefined })}
        {idLink(t('usage.filters.request_id'), e.request_id, { request_id: e.request_id ?? undefined })}
        {e.trace_id && (
          <div className="flex items-center gap-2">
            <span className="text-slate-500 w-20 shrink-0">trace</span>
            {traceUrl ? (
              <a
                href={traceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
              >
                {e.trace_id} <ExternalLink size={12} />
              </a>
            ) : (
              <span className="font-mono text-xs">{e.trace_id}</span>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-slate-500 w-20 shrink-0">{t('usage.provider')}</span>
          <span className="font-mono text-xs">
            {e.provider} / {e.model}
          </span>
        </div>
      </div>
      {Object.keys(e.extra).length > 0 && (
        <pre className="text-xs bg-white border border-slate-200 rounded-lg p-2 overflow-x-auto">
          {JSON.stringify(e.extra, null, 2)}
        </pre>
      )}
      {!e.priced && <p className="text-xs text-amber-700">{t('usage.unpricedRow')}</p>}
      {e.thread_id &&
        (canReadContent ? (
          showTurn ? (
            <TurnView eventId={e.id} />
          ) : (
            <button
              type="button"
              className="btn-secondary text-sm inline-flex items-center gap-2"
              onClick={() => setShowTurn(true)}
            >
              <MessageSquareText size={15} /> {t('usage.showTurn')}
            </button>
          )
        ) : (
          <p className="text-xs text-slate-500">{t('usage.turnAdminOnly')}</p>
        ))}
    </div>
  );
};

const TurnView: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation();
  const turn = useUsageTurn(eventId);
  if (turn.isLoading) return <Loader2 className="animate-spin text-slate-400" size={18} />;
  if (turn.isError || !turn.data) return <ErrorBox error={turn.error} fallback={t('usage.turnFailed')} />;
  const { user_message: u, assistant_message: a } = turn.data;
  return (
    <div className="space-y-2" data-testid="usage-turn">
      <p className="text-xs text-slate-500">{t('usage.turnAudited')}</p>
      {[
        [t('usage.userSaid'), u],
        [t('usage.assistantSaid'), a],
      ].map(([label, m]) =>
        m && typeof m === 'object' ? (
          <div key={m.id} className="bg-white border border-slate-200 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">
              {label as string} · {formatKstTime(m.timestamp)}
            </p>
            <p className="whitespace-pre-wrap text-slate-800">{m.content}</p>
          </div>
        ) : (
          <p key={label as string} className="text-xs text-slate-400">
            {label as string}: {t('usage.noMessage')}
          </p>
        ),
      )}
    </div>
  );
};

// --- 기준·기타 사항 ---------------------------------------------------------------------

export const PricesPanel: React.FC = () => {
  const { t } = useTranslation();
  const prices = useUsagePrices(true);
  if (prices.isLoading) return <Loading />;
  if (prices.isError || !prices.data) return <ErrorBox error={prices.error} fallback={t('usage.loadFailed')} />;
  const { items, notes, usd_krw_rate } = prices.data;

  const display = (meter: string, perUnit: number) =>
    meter === 'unit' ? `$${perUnit.toFixed(4)} / ${t('usage.perUnit')}` : `$${(perUnit * 1e6).toFixed(3)} / 1M`;

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">{t('usage.notesTitle')}</h3>
        <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
          <li>{t('usage.rateNote', { rate: usd_krw_rate.toLocaleString('ko-KR') })}</li>
          {notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      </div>
      <div className="card overflow-x-auto">
        <table className="w-full text-sm" data-testid="usage-prices-table">
          <thead className="bg-slate-50 text-xs text-slate-500 whitespace-nowrap">
            <tr>
              <th className="font-medium py-2 px-3 text-left">{t('usage.provider')}</th>
              <th className="font-medium py-2 px-3 text-left">{t('usage.cols.model')}</th>
              <th className="font-medium py-2 px-3 text-left">{t('usage.meter')}</th>
              <th className="font-medium py-2 px-3 text-right">{t('usage.price')}</th>
              <th className="font-medium py-2 px-3 text-left">{t('usage.condition')}</th>
              <th className="font-medium py-2 px-3 text-left">{t('usage.validFrom')}</th>
              <th className="font-medium py-2 px-3 text-left">{t('usage.source')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-1.5 px-3 font-mono text-xs">{p.provider}</td>
                <td className="py-1.5 px-3 font-mono text-xs">{p.model}</td>
                <td className="py-1.5 px-3">{t(`usage.meters.${p.meter}`, { defaultValue: p.meter })}</td>
                <td className="py-1.5 px-3 text-right tabular-nums">{display(p.meter, p.usd_per_unit)}</td>
                <td className="py-1.5 px-3 text-xs text-slate-600">
                  {p.condition ? JSON.stringify(p.condition) : '-'}
                  {p.note && <div className="text-slate-400">{p.note}</div>}
                </td>
                <td className="py-1.5 px-3 text-xs text-slate-600 tabular-nums">
                  {p.valid_from.slice(0, 10)}
                  {p.valid_until ? ` ~ ${p.valid_until.slice(0, 10)}` : ' ~'}
                </td>
                <td className="py-1.5 px-3 text-xs">
                  <a
                    href={p.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {hostOf(p.source_url)}
                  </a>
                  <span className="text-slate-400"> · {p.checked_on}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
